// Bearer token storage + auth hook.
//
// Token lives in localStorage under `protocol-api-token`. Stored once after
// the operator pastes it on first connect and verifies via `/api/whoami`.
// CSP `_headers` keeps third-party scripts off the origin (SEC-2), but
// localStorage remains the practical store for a single-user PWA.

import { useSyncExternalStore } from 'react';

const TOKEN_KEY = 'protocol-api-token';
const API_URL_KEY = 'protocol-api-url';

// In-process subscribers so multiple components re-render together when the
// token is set/cleared (e.g. token UI in Settings + the root auth gate).
type Listener = () => void;
const listeners = new Set<Listener>();
function emit() {
  for (const l of listeners) l();
}

export function getToken(): string | null {
  try {
    const t = localStorage.getItem(TOKEN_KEY);
    return t && t.length > 0 ? t : null;
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    emit();
  } catch {
    // Storage quota / private-mode failures shouldn't crash the app; surfaces
    // as "no token" on next read and the auth gate re-prompts.
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    emit();
  } catch {
    // Same rationale as setToken — swallow and move on.
  }
}

// Fingerprint shown in Settings — first 8 chars + ellipsis. Token format is a
// hex string from `scripts/gen-token.js`; the API computes its own fingerprint
// from the hash of the *whole* token, so we can't display that without a
// round-trip. The locally-computed first-8 is enough as a quick visual check.
export function tokenFingerprint(token: string | null): string {
  if (!token) return '';
  return `${token.slice(0, 8)}…`;
}

// ---- API base URL ----
// Resolution order (V1.1: API URL is baked at build time — there is one Worker
// per environment, so the form input was removed):
//   1. `import.meta.env.VITE_PROTOCOL_API_URL` — baked at build time from
//      `.env.production` / `.env.development`.
//   2. `localStorage['protocol-api-url']` — runtime escape hatch for pointing
//      a deployed PWA at a non-default Worker without rebuilding. Not exposed
//      in the UI; use the dev console.
//   3. Empty string — surfaces as a network error rather than a silent
//      localhost call against prod.

const DEFAULT_API_URL = '';

export function getApiUrl(): string {
  const envUrl = import.meta.env.VITE_PROTOCOL_API_URL;
  if (envUrl && envUrl.length > 0) return envUrl.replace(/\/$/, '');
  try {
    const v = localStorage.getItem(API_URL_KEY);
    if (v && v.length > 0) return v.replace(/\/$/, '');
  } catch {
    // ignore
  }
  return DEFAULT_API_URL;
}

export function setApiUrl(url: string): void {
  try {
    localStorage.setItem(API_URL_KEY, url.replace(/\/$/, ''));
    emit();
  } catch {
    // ignore
  }
}

// ---- React hook ----
// `useSyncExternalStore` integrates the in-memory subscriber set with React's
// concurrent rendering. Snapshot is the token string (or `null`).

function subscribe(cb: Listener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): string | null {
  return getToken();
}

function getServerSnapshot(): string | null {
  return null;
}

export function useAuth(): {
  token: string | null;
  isAuthed: boolean;
  fingerprint: string;
  signIn: (token: string) => void;
  signOut: () => void;
} {
  const token = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return {
    token,
    isAuthed: token !== null,
    fingerprint: tokenFingerprint(token),
    signIn: setToken,
    signOut: clearToken,
  };
}
